import { StoreAvailabilityProbabilityEnum } from '@inter-ikea-kompis/enums';
import type { IKompisTranslations, IStoreAvailability } from '@inter-ikea-kompis/types';

import type { StatusColour } from '@ingka/status-webc';

// Get all types which contains the replacement string [[store]] which will be replaced with the users selected store.
type IAvailabilityTranslation = Pick<
	IKompisTranslations,
	| 'storeAvailabilityInStock'
	| 'storeAvailabilitySomeLowOrOutOfStock'
	| 'storeAvailabilityLowInStock'
	| 'storeAvailabilityOrderItems'
	| 'storeAvailabilityOutOfStock'
	| 'storeAvailabilityNotSoldAtStore'
>;

/**
 * Availability utility.
 */
export default class AvailabilityUtility {
	/**
	 * Returns the availability status for the supplied product(s).
	 *
	 * There are four possible cases for availability.
	 * Only one product exists.
	 * Several storeAvailabilities exists, where the type of the storeAvailabilities is the same.
	 * Several storeAvailabilities exists, where the type of the storeAvailabilities is different.
	 * Null, if something unexpected occurs.
	 *
	 * @param availabilities Store availability information for individual products.
	 */
	public static getStoreAvailabilityStatus(
		availabilities: IStoreAvailability[]
	): StoreAvailabilityProbabilityEnum | null {
		if (availabilities.length === 1) {
			return this.getSingleAvailability(availabilities[0]);
		}

		const storeAvailabilities = this.mapAvailabiltiesToEnum(availabilities);

		if (storeAvailabilities.length === 0) {
			return null;
		}

		const findStoreAvailabilityWithMatchingTypes =
			this.findStoreAvailabilityWithMatchingTypes(storeAvailabilities);

		if (findStoreAvailabilityWithMatchingTypes) {
			return findStoreAvailabilityWithMatchingTypes;
		}

		const findAnyStoreAvailabilityType = this.findAnyStoreAvailabilityType(storeAvailabilities);

		if (findAnyStoreAvailabilityType) {
			return findAnyStoreAvailabilityType;
		}

		return null;
	}

	/**
	 * Returns status indicator color.
	 *
	 * @param storeAvailability Store availability.
	 * @returns Status indicator color.
	 */
	public static getStoreAvailabilityStatusColor(
		storeAvailability: IStoreAvailability | IStoreAvailability[]
	): StatusColour | null {
		const availability = this.getStoreAvailabilty(storeAvailability);
		if (availability) {
			return this.getStoreAvailabilityColor(availability);
		}
		return null;
	}

	/**
	 * Returns status indicator color for a given availability status.
	 *
	 * @param availabilityStatus
	 * @returns Status indicator color.
	 */
	public static getStoreAvailabilityColor(
		availabilityStatus: StoreAvailabilityProbabilityEnum
	): StatusColour | null {
		switch (availabilityStatus) {
			case StoreAvailabilityProbabilityEnum.highInStock:
				return 'green';
			case StoreAvailabilityProbabilityEnum.mediumInStock:
			case StoreAvailabilityProbabilityEnum.lowInStock:
			case StoreAvailabilityProbabilityEnum.someLowOrOutOfStock:
				return 'orange';
			case StoreAvailabilityProbabilityEnum.outOfStock:
			case StoreAvailabilityProbabilityEnum.notSoldAtStore:
				return 'red';
			case StoreAvailabilityProbabilityEnum.orderItemSingle:
			case StoreAvailabilityProbabilityEnum.orderItemMultiple:
				return 'grey';
			default:
				return null;
		}
	}

	/**
	 * Returns status indicator label.
	 *
	 * @param options Options.
	 * @param options.translations Localized information.
	 * @param options.storeAvailability Store availability.
	 * @returns Status indicator label.
	 */
	public static getStoreAvailabilityLabel(options: {
		translations: IAvailabilityTranslation;
		storeAvailability: IStoreAvailability | IStoreAvailability[];
	}): string | null {
		const status = this.getStoreAvailabilty(options.storeAvailability);
		return AvailabilityUtility.getStoreAvailabilityTranslation(status, options.translations);
	}

	/**
	 * Returns the store availability translation.
	 *
	 * @param availabilityStatus
	 * @param localizedInformation
	 * @param translations
	 * @returns Store availability translation string.
	 */
	public static getStoreAvailabilityTranslation(
		availabilityStatus: StoreAvailabilityProbabilityEnum | null,
		translations: IAvailabilityTranslation
	): string | null {
		switch (availabilityStatus) {
			case StoreAvailabilityProbabilityEnum.highInStock:
				return translations.storeAvailabilityInStock;
			case StoreAvailabilityProbabilityEnum.someLowOrOutOfStock:
				return translations.storeAvailabilitySomeLowOrOutOfStock;
			case StoreAvailabilityProbabilityEnum.mediumInStock:
			case StoreAvailabilityProbabilityEnum.lowInStock:
				return translations.storeAvailabilityLowInStock;
			case StoreAvailabilityProbabilityEnum.outOfStock:
				return translations.storeAvailabilityOutOfStock;
			case StoreAvailabilityProbabilityEnum.notSoldAtStore:
				return translations.storeAvailabilityNotSoldAtStore;
			case StoreAvailabilityProbabilityEnum.orderItemSingle:
			case StoreAvailabilityProbabilityEnum.orderItemMultiple:
				return translations.storeAvailabilityOrderItems;

			default:
				return null;
		}
	}

	/**
	 * @param storeAvailability
	 */
	private static getSingleAvailability(
		storeAvailability: IStoreAvailability
	): StoreAvailabilityProbabilityEnum | null {
		if (storeAvailability.content.cashCarry?.inRange === false) {
			if (storeAvailability.content.homeDelivery?.inRange === true) {
				return StoreAvailabilityProbabilityEnum.orderItemSingle;
			}

			return StoreAvailabilityProbabilityEnum.notSoldAtStore;
		}

		return storeAvailability.content?.cashCarry?.messageType ?? null;
	}

	/**
	 * Returns status indicator enum.
	 *
	 * @param storeAvailability Store availability.
	 * @returns StoreAvailabilityProbabilityEnum | null.
	 */
	private static getStoreAvailabilty(
		storeAvailability: IStoreAvailability | IStoreAvailability[] // Prop name and type not to introduce a breaking change from originally only supporting one availabiltity
	): StoreAvailabilityProbabilityEnum | null {
		if (Array.isArray(storeAvailability)) {
			return this.getStoreAvailabilityStatus(storeAvailability);
		}
		return this.getStoreAvailabilityStatus([storeAvailability]);
	}

	/**
	 * Maps the data returned from DEXF to our StoreAvailabilityProbabilityEnum[].
	 * Special business cases are also handled with this function.
	 *
	 * @param availabilities IStoreAvailability[].
	 */
	private static mapAvailabiltiesToEnum(
		availabilities: IStoreAvailability[]
	): StoreAvailabilityProbabilityEnum[] {
		const { notSoldAtStore } = StoreAvailabilityProbabilityEnum;

		return availabilities
			.map((storeAvailability) => {
				if (storeAvailability.content.cashCarry?.inRange === false) {
					if (storeAvailability.content.homeDelivery?.inRange === true) {
						return StoreAvailabilityProbabilityEnum.orderItemSingle;
					}

					return notSoldAtStore;
				}

				return storeAvailability.content.cashCarry?.messageType;
			})
			.filter((type): type is StoreAvailabilityProbabilityEnum => {
				return type !== undefined && Object.values(StoreAvailabilityProbabilityEnum).includes(type);
			});
	}

	/**
	 * Returns the matching StoreAvailabilityProbabilityEnum, see StoreAvailability flowchart for a clearer picture.
	 * Https://i-p-e-x.atlassian.net/wiki/spaces/KOMPIS/pages/36238426201/Component+flow+charts#Store-Availability.
	 *
	 * @param storeAvailabilities StoreAvailabilityProbabilityEnum[].
	 */
	private static findStoreAvailabilityWithMatchingTypes(
		storeAvailabilities: StoreAvailabilityProbabilityEnum[]
	): StoreAvailabilityProbabilityEnum | null {
		const {
			highInStock,
			mediumInStock,
			lowInStock,
			outOfStock,
			notSoldAtStore,
			orderItemSingle,
			orderItemMultiple
		} = StoreAvailabilityProbabilityEnum;

		if (storeAvailabilities.every((type) => type === orderItemSingle)) {
			return orderItemMultiple;
		} else if (storeAvailabilities.every((type) => type === notSoldAtStore)) {
			return notSoldAtStore;
		} else if (
			storeAvailabilities.every((type) => type === outOfStock || type === notSoldAtStore)
		) {
			return outOfStock;
		} else if (storeAvailabilities.some((type) => type === orderItemSingle)) {
			return orderItemMultiple;
		} else if (storeAvailabilities.every((type) => type === lowInStock || type === mediumInStock)) {
			return lowInStock;
		} else if (storeAvailabilities.every((type) => type === highInStock)) {
			return highInStock;
		}

		return null;
	}

	/**
	 * Returns the matching StoreAvailabilityProbabilityEnum, see StoreAvailability flowchart for a clearer picture.
	 * Https://i-p-e-x.atlassian.net/wiki/spaces/KOMPIS/pages/36238426201/Component+flow+charts#Store-Availability.
	 *
	 * @param storeAvailabilities StoreAvailabilityProbabilityEnum[].
	 */
	private static findAnyStoreAvailabilityType(
		storeAvailabilities: StoreAvailabilityProbabilityEnum[]
	): StoreAvailabilityProbabilityEnum | null {
		if (
			storeAvailabilities.some(
				(type) =>
					type === StoreAvailabilityProbabilityEnum.lowInStock ||
					type === StoreAvailabilityProbabilityEnum.outOfStock ||
					type === StoreAvailabilityProbabilityEnum.notSoldAtStore ||
					type === StoreAvailabilityProbabilityEnum.mediumInStock
			)
		) {
			return StoreAvailabilityProbabilityEnum.someLowOrOutOfStock;
		}

		return null;
	}
}
